/**
 * 이상한 방 - 소품 카탈로그 (통합 지점)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 에셋: `ASSETS.md` · `public/assets/models.json` · 시각 색인 `public/assets/catalog-preview.png`
 *
 * 이 파일은 **규격만** 정한다. 실제 형상은 `src/render/meshFactory.ts`가 만든다.
 * 둘을 갈라 둔 이유: 카탈로그는 서버 판정(점유 셀·회전 허용 여부)의 입력이고,
 * 형상은 클라이언트 렌더의 문제다. 서버는 메시를 몰라도 판정할 수 있어야 한다.
 *
 * ## 왜 44종에서 다시 늘렸는가
 *
 * `public/assets/models/`에 Kenney CC0 GLB가 196종 있는데 배선된 것은 44종(22%)뿐이었다.
 * 그 상태로 20m × 20m 실내를 391개로 채우면 **휴지통 43개 · 화분 43개**가 깔린다.
 * 같은 물건이 수십 개면 술래는 형태가 아니라 **개수 세기**로 풀게 되고,
 * 그 순간 컨셉시트 4.2가 말한 동사별 난이도 차이가 통째로 무너진다.
 * 남은 모델을 다섯 그룹이 나눠 배선했고, 이 파일은 그 결과를 **합치기만** 한다.
 *
 * ## 이 파일의 세 가지 책임
 *
 * 1. **기존 44종을 새 스키마로 승격한다.** `id`·`name`·`footprint`·`rotSymmetry`·`defaultColorId`·
 *    `tags`는 한 글자도 건드리지 않고(기존 테스트·에디터 단축키의 계약),
 *    비어 있던 `modelId`·`sizeClass`·`recolorSlot`·`weight`만 채운다.
 *    `modelId`는 `meshFactory.ts`의 레시피가 이미 쓰고 있는 파일명을 그대로 전사했다 -
 *    두 곳이 다른 GLB를 가리키면 "카탈로그가 말하는 물건과 화면에 뜨는 물건"이 갈라진다.
 * 2. **중복 실루엣을 떨어뜨린다.** 아래 `SUPERSEDED_DRAFT_IDS` 참조.
 * 3. **불변식을 모듈 로드 시점에 터뜨린다.** id가 겹칠 때 조용히 덮어쓰면 나중에
 *    "분명히 등록했는데 없는 소품"이 되어 원인 추적이 불가능해진다.
 *
 * ## 축 규약 (중요)
 *
 * `footprint`는 `Cell3`를 재사용하지만 좌표가 아니라 **각 축이 차지하는 셀 개수**다.
 * **y가 높이(위)이고 x·z가 바닥 평면**이다. 미터 값 = 셀 개수 × 0.25m.
 *
 * ## 방 태그(`tags`)는 무엇에 쓰는가
 *
 * 어느 구획에 어떤 소품이 올 수 있는지를 배치기가 알아야 한다. 변기를 주방에 놓는 것은
 * **콘텐츠**이므로(컨셉시트 5절) 금지가 아니라 **기본값**으로만 쓴다 -
 * 자동 배치기가 참고하는 힌트이지 규칙이 아니다. 그래서 게임 규칙을 다루는 `verbs.ts`는 이 필드를 보지 않는다.
 */

import type { RoomTag as FloorPlanRoomTag } from '../core/floorPlan'
import type { CatalogEntry, CatalogId } from '../core/types'
import type { CatalogEntryDraft } from './entryTypes'
import { assertEntryInvariants } from './entryTypes'
import { SEATING_BEDROOM_ENTRIES } from './entries/seatingBedroom'
import { TABLES_STORAGE_ENTRIES } from './entries/tablesStorage'
import { KITCHEN_BATH_ENTRIES } from './entries/kitchenBath'
import { DECOR_ELECTRONICS_ENTRIES } from './entries/decorElectronics'
import { TABLEWARE_FOOD_ENTRIES } from './entries/tablewareFood'

/**
 * 방 구획 종류. 자동 배치기의 힌트이며 게임 규칙(verbs.ts)은 이 값을 보지 않는다.
 *
 * 타입이 아니라 **배열이 원본**인 이유: 태그별 소품 풀이 충분한지 검사하려면 테스트가 태그를
 * 런타임에 순회할 수 있어야 하는데, 유니온 타입만 있으면 목록을 손으로 한 번 더 적게 되고
 * 그 사본은 반드시 뒤처진다 - 새 태그가 검사 없이 통과하는 경로가 그렇게 생긴다.
 *
 * 같은 유니온이 `core/floorPlan`에도 있다(코어가 카탈로그를 import하지 않으려는 의도).
 * `satisfies`가 "여기 있는 태그는 전부 평면도에도 있다"를, `catalog.test.ts`의 타입 단언이
 * 반대 방향을 지켜, 한쪽에만 태그를 더한 순간 tsc가 잡는다.
 */
export const ROOM_TAGS = [
  'living',
  'kitchen',
  'bedroom',
  'bath',
  'hall',
] as const satisfies readonly FloorPlanRoomTag[]

export type RoomTag = (typeof ROOM_TAGS)[number]

/**
 * 카탈로그 항목의 최소 계약 = 코어 규격(`CatalogEntry`) + 배치 힌트.
 *
 * `CatalogEntry`를 확장하는 형태인 이유: `tags`는 서버 판정에 쓰이지 않으므로
 * 코어 타입(`core/types.ts`)을 오염시키면 안 된다. 원칙 1(상태 필드는 변화 동사에서 역산한다)의
 * 같은 논리다 - 판정에 안 쓰는 필드는 판정 타입에 들어가지 않는다.
 *
 * 실제 `CATALOG` 원소는 이보다 넓은 `CatalogEntryDraft`(+ `modelId`·`sizeClass`·`recolorSlot`·`weight`)다.
 * 이 좁은 타입을 남겨 두는 이유는 `entryTypes.ts`가 여기서 파생되기 때문이며,
 * 소비자는 대부분 이만큼만 있으면 된다.
 */
export type CatalogItem = CatalogEntry & Readonly<{ tags: readonly RoomTag[] }>

/* --- 기존 44종 (id·name·footprint·rotSymmetry·defaultColorId·tags 동결) ---------------
   `modelId`는 meshFactory 레시피의 `model` 값을 그대로 옮긴 것이고,
   `recolorSlot`은 그 GLB의 머티리얼 중 **면적을 지배하는 슬롯**을 골랐다(전 슬롯을 한 색으로
   칠하면 물건이 단색 덩어리가 되어 실루엣 판독이 나빠진다). `weight`는 실내에서의 흔함이다. */
const LEGACY_ENTRIES: readonly CatalogEntryDraft[] = [
  /* 1주차 기본 10종 - 에디터 숫자 단축키가 **배열 인덱스 그대로**를 고르므로 순서가 UI 계약이다. */
  // 손잡이가 방향을 만든다 → 90° 회전이 전부 구별된다.
  { id: 'mug', name: '머그컵', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 1, defaultColorId: 9, tags: ['kitchen', 'living', 'bedroom'], modelId: 'mug', sizeClass: 'surface', recolorSlot: 'slot0', weight: 8 },
  // 책등과 책배가 서로 다르다 → 180° 회전도 구별된다.
  { id: 'book', name: '책', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 1, defaultColorId: 4, tags: ['living', 'bedroom', 'hall', 'bath'], modelId: 'book', sizeClass: 'surface', recolorSlot: 'carpetDarker', weight: 8 },
  // 원통. 4.2절이 명시적으로 지목한 "돌려도 아무 일도 안 일어나는" 소품 → 회전 동사 전면 비활성.
  { id: 'trash-bin', name: '휴지통', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 4, defaultColorId: 5, tags: ['living', 'kitchen', 'bath', 'bedroom', 'hall'], modelId: 'trashcan', sizeClass: 'floor', recolorSlot: 'metal', weight: 3 },
  // 원형 화분 + 수직 실루엣. 바닥 면적은 최소인데 높이만 3칸이라 실루엣이 소형 3종과 갈린다.
  { id: 'potted-plant', name: '화분', footprint: { x: 1, y: 3, z: 1 }, rotSymmetry: 4, defaultColorId: 3, tags: ['living', 'kitchen', 'hall', 'bedroom', 'bath'], modelId: 'plant-small-1', sizeClass: 'floor', recolorSlot: 'wood', weight: 3 },
  // 등받이가 앞뒤를 가른다. 바닥 면적은 정사각이지만 등받이 때문에 네 방향이 전부 다르다.
  { id: 'chair', name: '의자', footprint: { x: 2, y: 4, z: 2 }, rotSymmetry: 1, defaultColorId: 1, tags: ['living', 'kitchen'], modelId: 'chair', sizeClass: 'floor', recolorSlot: 'wood', weight: 7 },
  // 등받이 없는 긴 벤치 → 180° 돌리면 완전히 같다. 90°는 가로세로가 바뀌어 확연히 보인다.
  { id: 'bench', name: '벤치', footprint: { x: 6, y: 2, z: 2 }, rotSymmetry: 2, defaultColorId: 2, tags: ['hall'], modelId: 'bench-cushion-low', sizeClass: 'floor', recolorSlot: 'carpet', weight: 5 },
  // 직사각 상판 → 180° 대칭. 4.2절의 "180° 회전 거부, 90°만 허용" 경로를 밟는 대표 소품.
  { id: 'table-dining', name: '식탁', footprint: { x: 6, y: 3, z: 4 }, rotSymmetry: 2, defaultColorId: 7, tags: ['kitchen', 'living'], modelId: 'table-dining', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  // 한쪽 면만 열려 있다 → 네 방향 전부 구별. 방에서 가장 높은 소품이라 은폐물로도 쓰인다.
  { id: 'shelf-tall', name: '책장', footprint: { x: 4, y: 7, z: 2 }, rotSymmetry: 1, defaultColorId: 6, tags: ['living'], modelId: 'shelf-tall', sizeClass: 'anchor', recolorSlot: 'wood', weight: 5 },
  // 등받이 + 팔걸이. 방에서 가장 넓은 가구이며 "내 소파 누가 옮겼어"의 주인공(컨셉시트 5절).
  { id: 'sofa', name: '소파', footprint: { x: 8, y: 3, z: 4 }, rotSymmetry: 1, defaultColorId: 8, tags: ['living'], modelId: 'lounge-sofa', sizeClass: 'anchor', recolorSlot: 'carpet', weight: 4 },
  // 원형 → 완전 대칭. 높이 1칸은 물리적 두께가 아니라 그리드 최소 단위다(셀보다 얇을 수 없다).
  { id: 'rug-round', name: '원형 러그', footprint: { x: 6, y: 1, z: 6 }, rotSymmetry: 4, defaultColorId: 0, tags: ['living'], modelId: 'rug-round', sizeClass: 'floor', recolorSlot: 'carpet', weight: 3 },

  /* 초소형 1×1×1 - 책상·선반 위 밀도를 담당한다. */
  { id: 'cup', name: '컵', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 4, defaultColorId: 9, tags: ['kitchen', 'bath', 'bedroom'], modelId: 'cup', sizeClass: 'surface', recolorSlot: 'slot0', weight: 7 },
  { id: 'plate', name: '접시', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 4, defaultColorId: 8, tags: ['kitchen'], modelId: 'plate-dinner', sizeClass: 'surface', recolorSlot: 'slot0', weight: 7 },
  { id: 'bottle', name: '병', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 4, defaultColorId: 2, tags: ['kitchen', 'bath', 'bedroom'], modelId: 'bottle-oil', sizeClass: 'surface', recolorSlot: 'slot0', weight: 6 },
  // 비대칭은 몸통이 아니라 **버튼 쪽 경사**가 만든다 → meshFactory에서 한쪽으로 치우쳐 놓는다.
  { id: 'computer-mouse', name: '마우스', footprint: { x: 1, y: 1, z: 1 }, rotSymmetry: 1, defaultColorId: 0, tags: ['living', 'bedroom'], modelId: 'computer-mouse', sizeClass: 'surface', recolorSlot: 'metalDark', weight: 4 },

  /* 소형 - 밀도의 주력. 400개를 채울 때 실제로 가장 많이 놓이는 구간. */
  { id: 'keyboard', name: '키보드', footprint: { x: 3, y: 1, z: 1 }, rotSymmetry: 1, defaultColorId: 0, tags: ['living', 'bedroom'], modelId: 'computer-keyboard', sizeClass: 'surface', recolorSlot: 'metalDark', weight: 4 },
  // 직사각 매트. 90°는 가로세로가 바뀌어 확연하지만 180°는 무변화다.
  { id: 'doormat', name: '현관매트', footprint: { x: 4, y: 1, z: 2 }, rotSymmetry: 2, defaultColorId: 5, tags: ['hall'], modelId: 'rug-doormat', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'pillow', name: '쿠션', footprint: { x: 2, y: 2, z: 1 }, rotSymmetry: 2, defaultColorId: 7, tags: ['bedroom', 'living', 'hall'], modelId: 'pillow', sizeClass: 'surface', recolorSlot: 'carpet', weight: 6 },
  { id: 'laptop', name: '노트북', footprint: { x: 3, y: 2, z: 2 }, rotSymmetry: 1, defaultColorId: 2, tags: ['living'], modelId: 'laptop', sizeClass: 'surface', recolorSlot: 'metalDark', weight: 4 },
  { id: 'microwave', name: '전자레인지', footprint: { x: 4, y: 2, z: 3 }, rotSymmetry: 1, defaultColorId: 9, tags: ['kitchen'], modelId: 'kitchen-microwave', sizeClass: 'surface', recolorSlot: 'metalMedium', weight: 3 },
  // 원형 갓 + 정사각 바닥면. 4.2절의 "정사각 협탁" 계열 - 회전이 전면 잠긴다.
  { id: 'lamp-table', name: '탁상등', footprint: { x: 2, y: 3, z: 2 }, rotSymmetry: 4, defaultColorId: 9, tags: ['living', 'bedroom', 'hall'], modelId: 'lamp-round-table', sizeClass: 'surface', recolorSlot: 'metal', weight: 4 },
  { id: 'nightstand', name: '협탁', footprint: { x: 3, y: 3, z: 2 }, rotSymmetry: 1, defaultColorId: 5, tags: ['bedroom'], modelId: 'cabinet-bed-drawer', sizeClass: 'floor', recolorSlot: 'wood', weight: 5 },
  { id: 'monitor', name: '모니터', footprint: { x: 4, y: 3, z: 1 }, rotSymmetry: 1, defaultColorId: 0, tags: ['living', 'bedroom'], modelId: 'computer-screen', sizeClass: 'surface', recolorSlot: 'metalDark', weight: 4 },
  { id: 'cardboard-box', name: '택배상자', footprint: { x: 3, y: 3, z: 3 }, rotSymmetry: 1, defaultColorId: 8, tags: ['hall', 'bedroom'], modelId: 'cardboard-box-closed', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'stool-bar', name: '바스툴', footprint: { x: 3, y: 4, z: 3 }, rotSymmetry: 4, defaultColorId: 1, tags: ['kitchen'], modelId: 'stool-bar', sizeClass: 'floor', recolorSlot: 'carpet', weight: 5 },
  { id: 'speaker', name: '스피커', footprint: { x: 2, y: 5, z: 2 }, rotSymmetry: 1, defaultColorId: 0, tags: ['living'], modelId: 'speaker', sizeClass: 'floor', recolorSlot: 'wood', weight: 3 },
  // 벽에 거는 얇은 판 → sizeClass도 벽면이다. z가 1칸이라 90° 회전은 확연하고 180°는 앞뒤가 뒤집혀 보인다.
  { id: 'mirror', name: '거울', footprint: { x: 3, y: 5, z: 1 }, rotSymmetry: 1, defaultColorId: 8, tags: ['bath', 'hall'], modelId: 'bathroom-mirror', sizeClass: 'wall', recolorSlot: 'wood', weight: 5 },

  /* 중형 - 구획의 성격을 만든다. 여기서 "주방인가 침실인가"가 읽힌다. */
  { id: 'side-table', name: '사이드테이블', footprint: { x: 5, y: 4, z: 2 }, rotSymmetry: 1, defaultColorId: 5, tags: ['living'], modelId: 'side-table-drawers', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'cabinet-tv', name: 'TV장', footprint: { x: 7, y: 3, z: 2 }, rotSymmetry: 1, defaultColorId: 5, tags: ['living'], modelId: 'cabinet-television', sizeClass: 'anchor', recolorSlot: 'wood', weight: 3 },
  { id: 'television', name: '텔레비전', footprint: { x: 6, y: 4, z: 2 }, rotSymmetry: 1, defaultColorId: 0, tags: ['living'], modelId: 'television-modern', sizeClass: 'floor', recolorSlot: 'metalDark', weight: 3 },
  { id: 'desk', name: '책상', footprint: { x: 6, y: 4, z: 4 }, rotSymmetry: 1, defaultColorId: 5, tags: ['living', 'bedroom'], modelId: 'desk', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'kitchen-cabinet', name: '주방수납장', footprint: { x: 4, y: 4, z: 4 }, rotSymmetry: 1, defaultColorId: 9, tags: ['kitchen'], modelId: 'kitchen-cabinet-drawer', sizeClass: 'anchor', recolorSlot: 'wood', weight: 5 },
  { id: 'kitchen-stove', name: '가스레인지', footprint: { x: 5, y: 4, z: 4 }, rotSymmetry: 1, defaultColorId: 8, tags: ['kitchen'], modelId: 'kitchen-stove', sizeClass: 'anchor', recolorSlot: 'metal', weight: 3 },
  { id: 'toilet', name: '변기', footprint: { x: 3, y: 4, z: 4 }, rotSymmetry: 1, defaultColorId: 9, tags: ['bath'], modelId: 'toilet', sizeClass: 'floor', recolorSlot: 'carpetWhite', weight: 3 },
  { id: 'bathroom-sink', name: '세면대', footprint: { x: 3, y: 5, z: 3 }, rotSymmetry: 1, defaultColorId: 9, tags: ['bath'], modelId: 'bathroom-sink', sizeClass: 'floor', recolorSlot: 'carpetWhite', weight: 4 },
  { id: 'chair-desk', name: '데스크체어', footprint: { x: 3, y: 6, z: 3 }, rotSymmetry: 1, defaultColorId: 1, tags: ['living', 'bedroom'], modelId: 'chair-desk', sizeClass: 'floor', recolorSlot: 'carpet', weight: 4 },
  { id: 'plant-large', name: '큰 화분', footprint: { x: 2, y: 6, z: 2 }, rotSymmetry: 4, defaultColorId: 3, tags: ['living', 'hall'], modelId: 'potted-plant', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'lamp-floor', name: '플로어램프', footprint: { x: 2, y: 7, z: 2 }, rotSymmetry: 4, defaultColorId: 8, tags: ['living', 'bedroom'], modelId: 'lamp-round-floor', sizeClass: 'floor', recolorSlot: 'metal', weight: 3 },

  /* 대형 - 4.1절의 "큰 물건일수록 1칸 이동이 안 보인다"를 관측할 재료다. */
  { id: 'bookcase-open', name: '오픈책장', footprint: { x: 4, y: 8, z: 2 }, rotSymmetry: 1, defaultColorId: 5, tags: ['living', 'bedroom'], modelId: 'bookcase-open', sizeClass: 'anchor', recolorSlot: 'wood', weight: 4 },
  { id: 'wardrobe', name: '옷장', footprint: { x: 7, y: 7, z: 2 }, rotSymmetry: 1, defaultColorId: 5, tags: ['bedroom'], modelId: 'bookcase-closed-wide', sizeClass: 'anchor', recolorSlot: 'wood', weight: 4 },
  { id: 'coat-rack', name: '코트걸이', footprint: { x: 3, y: 7, z: 3 }, rotSymmetry: 4, defaultColorId: 5, tags: ['hall'], modelId: 'coat-rack-standing', sizeClass: 'floor', recolorSlot: 'wood', weight: 4 },
  { id: 'fridge', name: '냉장고', footprint: { x: 4, y: 8, z: 3 }, rotSymmetry: 1, defaultColorId: 9, tags: ['kitchen'], modelId: 'kitchen-fridge', sizeClass: 'anchor', recolorSlot: 'metalLight', weight: 3 },
  { id: 'bed-double', name: '침대', footprint: { x: 8, y: 3, z: 9 }, rotSymmetry: 1, defaultColorId: 7, tags: ['bedroom'], modelId: 'bed-double', sizeClass: 'anchor', recolorSlot: 'carpetWhite', weight: 3 },
  { id: 'bathtub', name: '욕조', footprint: { x: 10, y: 4, z: 5 }, rotSymmetry: 1, defaultColorId: 9, tags: ['bath'], modelId: 'bathtub', sizeClass: 'anchor', recolorSlot: 'carpetWhite', weight: 3 },
  { id: 'rug-rectangle', name: '사각 러그', footprint: { x: 13, y: 1, z: 8 }, rotSymmetry: 2, defaultColorId: 6, tags: ['living', 'bedroom'], modelId: 'rug-rectangle', sizeClass: 'floor', recolorSlot: 'carpet', weight: 3 },
]

/**
 * 그룹 초안 중 **채택하지 않는** id.
 *
 * 이유는 하나뿐이다 - 기존 44종이 이미 쓰고 있는 GLB와 **같은 물건**이기 때문이다.
 * `assertEntryInvariants`는 `modelId` 문자열이 겹칠 때만 잡아내는데, Kenney 배포본에는
 * 파일명만 다르고 **바이트가 완전히 같은 별칭이 5쌍** 있어서(`ASSETS.md` 요약의 "레거시 별칭 5개")
 * 문자열 비교만으로는 통과해 버린다. 그러면 이름이 둘인 같은 물건이 카탈로그에 남고,
 * 그건 8절이 금지한 실루엣 중복 그 자체다.
 *
 * 기존 id는 동결이므로 **탈락하는 쪽은 항상 새 초안**이다.
 * 이 목록이 정말 중복인지(= 임의로 소품을 빼는 뒷문이 아닌지)는
 * `catalog.test.ts`가 GLB 내용 해시로 직접 검증한다.
 */
export const SUPERSEDED_DRAFT_IDS: ReadonlySet<CatalogId> = new Set<CatalogId>([
  'plate-dinner', // = 기존 'plate' (같은 modelId 'plate-dinner')
  'bottle-oil', //  = 기존 'bottle' (같은 modelId 'bottle-oil')
  'book-stack', //  modelId 'books' 가 기존 'book' 의 'book.glb' 와 바이트 동일
])

/** 다섯 그룹이 작성한 초안 전체(탈락분 포함). 순서가 곧 `CATALOG`의 뒷부분 순서다. */
const GROUP_DRAFTS: readonly CatalogEntryDraft[] = [
  ...SEATING_BEDROOM_ENTRIES,
  ...TABLES_STORAGE_ENTRIES,
  ...KITCHEN_BATH_ENTRIES,
  ...DECOR_ELECTRONICS_ENTRIES,
  ...TABLEWARE_FOOD_ENTRIES,
]

/**
 * 소품 카탈로그 전체. 앞쪽 44개는 순서까지 동결이다 -
 * 에디터의 숫자 단축키가 **배열 인덱스 그대로**를 고르므로(`input/editor.ts`) 순서가 UI 계약이다.
 * 새 소품은 반드시 뒤에 붙는다.
 */
export const CATALOG: readonly CatalogEntryDraft[] = [
  ...LEGACY_ENTRIES,
  ...GROUP_DRAFTS.filter((draft) => !SUPERSEDED_DRAFT_IDS.has(draft.id)),
]

/**
 * 모듈 로드 시점 검증. 여기서 던지면 앱도 테스트도 **시작 전에** 멈춘다.
 *
 * 미루면 안 되는 이유: id가 겹칠 때 `CATALOG_BY_ID`는 조용히 뒤엣것으로 덮어쓰고,
 * 그 결과는 "카탈로그에는 있는데 조회하면 다른 물건이 나오는" 상태다.
 * 배치·렌더·판정이 전부 이 맵을 거치므로 증상은 한참 떨어진 곳에서 나타난다.
 */
assertEntryInvariants(CATALOG)

/** 탈락 목록의 오타 방어. 존재하지 않는 id를 적어 두면 중복이 그대로 살아남는다. */
const droppedCount = GROUP_DRAFTS.length - (CATALOG.length - LEGACY_ENTRIES.length)
if (droppedCount !== SUPERSEDED_DRAFT_IDS.size) {
  throw new Error(
    `SUPERSEDED_DRAFT_IDS에 실재하지 않는 id가 있다: 선언 ${SUPERSEDED_DRAFT_IDS.size}건 / 실제 탈락 ${droppedCount}건`,
  )
}

/**
 * id → 항목 조회용 인덱스.
 * 배치·검증 경로에서 매 프레임 선형 탐색을 돌리지 않기 위해 모듈 로드 시 한 번만 만든다.
 */
export const CATALOG_BY_ID: ReadonlyMap<CatalogId, CatalogEntryDraft> = new Map(
  CATALOG.map((entry) => [entry.id, entry] as const),
)

/**
 * 카탈로그 항목 조회. 없는 id면 `undefined`.
 *
 * 팔레트의 `getColor()`가 던지는 것과 달리 여기서 던지지 않는 이유:
 * `catalogId`는 클라이언트 페이로드나 저장된 방 스냅샷에서 들어오는 **외부 데이터**이고,
 * 카탈로그 버전이 갈리면 "모르는 소품"은 정상적으로 발생 가능한 상황이다.
 * 호출부가 스킵할지 거부할지 결정해야 하므로 판단을 넘긴다.
 */
export function getCatalogEntry(id: CatalogId): CatalogEntryDraft | undefined {
  return CATALOG_BY_ID.get(id)
}
